// Valou Service - Berechnung und Matching von Valou-Bereichen

import { doc, getDoc } from '@firebase/firestore';
import { db } from './firebase';
import type { Place } from '../types';
import type { ValouAreaId, ValouProfile, PlaceMatchScore } from '../types/valou';
import { VALOU_AREAS, getValouAreaConfig } from '../types/valou';
import { VALOU_PLACE_MAPPINGS, VALOU_MATCH_THRESHOLD } from '../constants/valouMapping';

// ============================================
// PLACE VALOU CLASSIFICATION
// ============================================

/**
 * Berechnet die Valou-Bereiche fuer einen Ort basierend auf seinen Attributen
 */
export function computeValouAreasForPlace(place: Place): ValouAreaId[] {
  const matchedAreas: ValouAreaId[] = [];

  for (const [areaId, mapping] of Object.entries(VALOU_PLACE_MAPPINGS)) {
    let score = 0;

    // Moods matchen
    for (const mood of mapping.moods) {
      if (place.moods.includes(mood)) {
        score += 1;
      }
    }

    // Activities matchen
    for (const activity of mapping.activities) {
      if (place.activities.includes(activity)) {
        score += 1;
      }
    }

    // Community Features matchen
    for (const feature of mapping.community) {
      if (place.community.includes(feature)) {
        score += 1;
      }
    }

    // Wenn Score >= Threshold, dann passt der Bereich
    if (score >= VALOU_MATCH_THRESHOLD) {
      matchedAreas.push(areaId as ValouAreaId);
    }
  }

  return matchedAreas;
}

/**
 * Gibt die Valou-Bereiche eines Ortes zurueck
 * Prioritaet: manuell > berechnet
 */
export function getPlaceValouAreas(place: Place): ValouAreaId[] {
  // Manuell gesetzte Bereiche haben Prioritaet
  if (place.valouAreas && place.valouAreas.length > 0) {
    return place.valouAreas;
  }

  // Berechnete Bereiche als Fallback
  if (place.computedValouAreas && place.computedValouAreas.length > 0) {
    return place.computedValouAreas;
  }

  // Live berechnen wenn nichts gespeichert
  return computeValouAreasForPlace(place);
}

// ============================================
// USER VALOU PROFILE (aus Crafting App)
// ============================================

/**
 * Laedt das Valou-Profil eines Users aus Firestore
 * Die Daten werden in der GoodWork Crafting App erstellt und gespeichert
 */
export async function getValouProfile(userId: string): Promise<ValouProfile | null> {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    return userData?.valouZielstylingData || null;
  } catch (error) {
    console.error('Error fetching Valou profile:', error);
    return null;
  }
}

/**
 * Prueft ob ein User ein Valou-Profil hat
 */
export async function hasValouProfile(userId: string): Promise<boolean> {
  const profile = await getValouProfile(userId);
  if (!profile) return false;

  // Pruefen ob mindestens ein Bereich Daten hat
  return Object.values(profile).some(
    (area) =>
      area &&
      (area.vorlieben?.length > 0 ||
        area.mustHaves?.length > 0 ||
        area.stylingSatz?.trim().length > 0)
  );
}

/**
 * Extrahiert die Mood-Werte pro Bereich aus dem Profil
 */
export function getValouMoods(profile: ValouProfile): Record<ValouAreaId, number> {
  const moods: Record<string, number> = {};

  for (const areaId of VALOU_AREAS.map((a) => a.id)) {
    const areaData = profile[areaId];
    moods[areaId] = areaData?.mood ?? 5; // Default: neutral (5)
  }

  return moods as Record<ValouAreaId, number>;
}

// ============================================
// PLACE-USER MATCHING
// ============================================

/**
 * Berechnet den Match-Score zwischen einem Ort und einem User-Profil
 */
export function calculateMatchScore(
  place: Place,
  userProfile: ValouProfile,
  userMoods?: Record<ValouAreaId, number>
): PlaceMatchScore {
  const placeAreas = getPlaceValouAreas(place);
  const moods = userMoods || getValouMoods(userProfile);

  let totalScore = 0;
  const matchedAreas: ValouAreaId[] = [];
  const improvementPotential: ValouAreaId[] = [];

  for (const areaId of placeAreas) {
    const userAreaData = userProfile[areaId];
    const userMood = moods[areaId] || 5;

    // Basis-Score fuer jeden matchenden Bereich
    let areaScore = 15;

    // Bonus wenn User Daten in diesem Bereich hat
    if (userAreaData) {
      if (userAreaData.vorlieben?.length > 0) areaScore += 5;
      if (userAreaData.mustHaves?.length > 0) areaScore += 5;
    }

    // Niedrige Mood-Werte erhoehen den Score (Ort koennte helfen)
    if (userMood <= 4) {
      areaScore += 10;
      improvementPotential.push(areaId);
    } else if (userMood <= 6) {
      areaScore += 5;
    }

    totalScore += areaScore;
    matchedAreas.push(areaId);
  }

  // Normalisieren auf 0-100
  const maxPossibleScore = 6 * 35; // 6 Bereiche * max 35 Punkte
  const normalizedScore = Math.min(100, Math.round((totalScore / maxPossibleScore) * 100));

  return {
    placeId: place.id,
    totalScore: normalizedScore,
    matchedAreas,
    improvementPotential,
  };
}

/**
 * Sortiert Orte nach Match-Score
 */
export function sortPlacesByMatch(
  places: Place[],
  userProfile: ValouProfile,
  userMoods?: Record<ValouAreaId, number>
): Array<Place & { matchScore: PlaceMatchScore }> {
  const moods = userMoods || getValouMoods(userProfile);

  return places
    .map((place) => ({
      ...place,
      matchScore: calculateMatchScore(place, userProfile, moods),
    }))
    .sort((a, b) => b.matchScore.totalScore - a.matchScore.totalScore);
}

/**
 * Findet Orte die bei niedrigen Mood-Werten helfen koennten
 */
export function getPlacesForMoodImprovement(
  places: Place[],
  userProfile: ValouProfile,
  userMoods: Record<ValouAreaId, number>
): Map<ValouAreaId, Place[]> {
  const suggestions = new Map<ValouAreaId, Place[]>();

  // Finde Bereiche mit niedrigem Mood (≤4)
  const lowMoodAreas = Object.entries(userMoods)
    .filter(([_, mood]) => mood <= 4)
    .map(([areaId]) => areaId as ValouAreaId);

  for (const areaId of lowMoodAreas) {
    // Finde Orte die diesen Bereich unterstuetzen
    const relevantPlaces = places
      .filter((place) => {
        const placeAreas = getPlaceValouAreas(place);
        return placeAreas.includes(areaId);
      })
      .slice(0, 3); // Max 3 pro Bereich

    if (relevantPlaces.length > 0) {
      suggestions.set(areaId, relevantPlaces);
    }
  }

  return suggestions;
}

// Re-export helper
export { getValouAreaConfig };
